import React, { useRef } from "react";
import useA11y from "apps/website/hooks/useA11y";
import TextBody from "apps/website/components/base/Text/TextBody/TextBody";
import {
  columnMap,
  gapMap,
  gapXMap,
  gapYMap,
} from "apps/website/maps/Grid.map";
import cx from "apps/website/utils/misc/cx";
import {
  useArrowOffset,
} from "apps/website/components/feature/Tooltip/hooks/UseArrowOffset";

import DefaultOption from "../DefaultOption/DefaultOption";
import CardOption from "../CardOption/CardOption";
import ListOption from "../ListOption/ListOption";
import SpotlightOption from "../SpotlightOption/SpotlightOption";
import {
  IBaseRadioOption,
  IDefaultOption,
  ICardOption,
  IListOption,
  IRadioGroupProps,
  ISpotlightOption,
  isRadioCardOptions,
  isRadioDefaultOptions,
  isRadioSpotlightOptions,
  isRadioListOptions,
} from "../RadioButtons.types";
import { addTooltipArrowOffset } from "../utils/addTooltipArrowOffset";
import { defaultJustifyClassMap } from "apps/website/maps/Flex.map";
import { marginBottomMap } from "apps/website/maps/Spacing.map";

// Helper function to render the appropriate option component
function renderOption(option: IBaseRadioOption, ref?: React.Ref<HTMLDivElement>) {
  switch (option.component) {
    case "card":
      return <CardOption {...(option as ICardOption)} ref={ref} />;
    case "list":
      return <ListOption {...(option as IListOption)} />;
    case "spotlight":
      return <SpotlightOption {...(option as ISpotlightOption)} ref={ref} />;
    case "default":
    default:
      return <DefaultOption {...(option as IDefaultOption)} ref={ref} />;
  }
}

function renderStackChildren(
  isStack: boolean,
  selected: boolean,
  option: IBaseRadioOption,
  selectedOptionChildrenStyle: string,
): React.ReactNode {
  if (isStack && selected && option.children) {
    return <div className={selectedOptionChildrenStyle}>{ option.children }</div>;
  }
  return null;
}

function renderInLineChildren(
  isInline: boolean,
  selectedOption: IBaseRadioOption | undefined,
  selectedOptionChildrenStyle: string,
  arrowOffsetPx?: number,
): React.ReactNode {
  if (isInline && selectedOption?.children) {
    return <div className={selectedOptionChildrenStyle}>
      { addTooltipArrowOffset(selectedOption.children, arrowOffsetPx) }
    </div>;
  }
  return null;
}

function getGroupStyle(styleOptions: IRadioGroupProps<IBaseRadioOption>["styleOptions"]) {
  return cx(
    "grid",
    styleOptions?.columns ? columnMap[styleOptions.columns] : "grid-cols-1",
    styleOptions?.gap ? gapMap[styleOptions.gap] : "gap-4",
    styleOptions?.gapX ? gapXMap[styleOptions.gapX] : "",
    styleOptions?.gapY ? gapYMap[styleOptions.gapY] : "",
  );
}

const RadioGroup = <T extends IBaseRadioOption>({
  name,
  selectedValue,
  options,
  legend,
  onChange,
  styleOptions,
  srOnlyLegend = false,
}: IRadioGroupProps<T>) => {
  const { UUID } = useA11y();
  const selectedOption = options.find((option) => option.value === selectedValue);

  const isInline =
    isRadioDefaultOptions(options) ||
    isRadioCardOptions(options) ||
    isRadioSpotlightOptions(options);

  const isStack = isRadioListOptions(options);

  const groupStyle = getGroupStyle(styleOptions);

  const selectedOptionRef = useRef<HTMLDivElement>(null);
  const gridContainerRef = useRef<HTMLDivElement>(null);

  const arrowOffsetPx = useArrowOffset(
    selectedOptionRef,
    gridContainerRef,
    [ selectedValue ],
  );

  const selectedOptionChildrenStyle = cx(
    "col-span-full",
    selectedOption?.childrenWrapperClassName,
    isStack && "mt-2",
  );

  return (
    <fieldset data-component={RadioGroup.name} data-theme="form-input" className="w-full">
      { legend && (
        <legend
          id={UUID}
          className={cx(
            "w-full flex",
            defaultJustifyClassMap[styleOptions?.label?.justify ?? "center"],
            marginBottomMap[styleOptions?.label?.marginBottom ?? 2],
            srOnlyLegend && "sr-only",
          )}
        >
          <TextBody size={1}>{ legend }</TextBody>
        </legend>
      ) }
      <div className={groupStyle} ref={gridContainerRef}>
        { options.map((option) => {
          const optionId = `${UUID}-${option.value}`;
          const selected = selectedValue === option.value;

          return (
            <div key={option.value}>
              <label htmlFor={optionId} className="cursor-pointer w-full">
                <input
                  type="radio"
                  id={optionId}
                  name={name}
                  value={option.value}
                  checked={selected}
                  onChange={() => onChange(option.value)}
                  disabled={option.disabled}
                  className="sr-only peer"
                />
                <div className="h-full rounded-xl peer-focus-visible:ring-4 peer-focus-visible:ring-brand peer-focus-visible:ring-offset-2">
                  { renderOption({ ...option, selected }, selected ? selectedOptionRef : undefined) }
                  { renderStackChildren(
                    isStack,
                    selected,
                    option,
                    selectedOptionChildrenStyle,
                  ) }
                </div>
              </label>
            </div>
          );
        }) }
        { renderInLineChildren(
          isInline,
          selectedOption,
          selectedOptionChildrenStyle,
          arrowOffsetPx,
        ) }
      </div>
    </fieldset>
  );
};

export default RadioGroup;
